import { formatJobNumber } from '@fieldops/shared'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '../../lib/cn.js'
import { formatTime, relativeDay } from '../../lib/format.js'
import { useJobHref } from '../../lib/job-overlay.js'
import { useSession } from '../../lib/session.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import { NewJobDialog } from '../NewJobDialog.js'
import { Avatar, EmptyState, ErrorState, SkeletonRows } from '../ui.js'
import {
  FieldScreen,
  FieldSearch,
  Group,
  ScreenHeader,
  Segmented,
  VisitBadge,
  cardClass,
  lengthOf,
  rowClass,
  workAndSuburb,
} from './kit.js'

type Visit = RouterOutputs['schedule']['board'][number]
type Scope = 'mine' | 'everyone'

const DAYS = 7

function weekWindow(): { from: Date; to: Date } {
  const from = new Date()
  from.setHours(0, 0, 0, 0)
  const to = new Date(from)
  to.setDate(to.getDate() + DAYS)
  return { from, to }
}

function matches(visit: Visit, term: string): boolean {
  if (!term) return true
  const address = visit.job.site?.address
  const haystack = [
    visit.job.client.name,
    address?.street,
    address?.suburb,
    address?.city,
    address?.raw,
    formatJobNumber(visit.job),
    visit.job.category?.name,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return term
    .toLowerCase()
    .split(/\s+/)
    .every((word) => haystack.includes(word))
}

/**
 * The week, a day at a time.
 *
 * A technician's Jobs tab is the bookings they have coming, not the office's
 * worklist: the question in the van is "what is on this week", and a list of
 * quotes waiting for replies is not an answer to it. The office gets Everyone
 * beside Mine, and the worklist is still one tap away from Me.
 */
export function FieldJobs() {
  const trpc = useTRPC()
  const { user, can } = useSession()
  const jobHref = useJobHref()
  const everyoneAllowed = can('SCHEDULE_VIEW_ALL')
  const [scope, setScope] = useState<Scope>(everyoneAllowed ? 'everyone' : 'mine')
  const [term, setTerm] = useState('')
  const [newJobOpen, setNewJobOpen] = useState(false)

  const range = useMemo(weekWindow, [])
  const board = useQuery({
    ...trpc.schedule.board.queryOptions(range),
    enabled: can('SCHEDULE_VIEW'),
  })

  const showEveryone = everyoneAllowed && scope === 'everyone'
  const visits = (board.data ?? []).filter(
    (visit) =>
      visit.status !== 'CANCELLED' &&
      visit.status !== 'NO_SHOW' &&
      (showEveryone || visit.assignments.some((assignment) => assignment.userId === user?.id)) &&
      matches(visit, term.trim()),
  )

  const days = new Map<string, { date: Date; visits: Visit[] }>()
  const ordered = [...visits].sort(
    (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime(),
  )
  for (const visit of ordered) {
    const date = new Date(visit.startAt)
    const key = date.toDateString()
    const day = days.get(key) ?? { date, visits: [] }
    day.visits.push(visit)
    days.set(key, day)
  }

  return (
    <FieldScreen>
      <ScreenHeader
        eyebrow={`${showEveryone ? 'Everyone' : (user?.name ?? 'Mine')} · this week`}
        title="Jobs"
        action={
          can('JOB_EDIT') && everyoneAllowed ? (
            <button
              type="button"
              onClick={() => setNewJobOpen(true)}
              className="flex h-9 items-center gap-1 rounded-md bg-brand-500 pr-3.5 pl-2.5 text-[15px] font-semibold text-ink-900 active:bg-brand-550"
            >
              <Icon name="plus" className="size-[18px]" />
              New job
            </button>
          ) : null
        }
      />

      {everyoneAllowed ? (
        <Segmented
          label="Whose jobs"
          value={scope}
          onChange={setScope}
          options={[
            { value: 'mine', label: 'Mine' },
            { value: 'everyone', label: 'Everyone' },
          ]}
        />
      ) : null}

      <FieldSearch value={term} onChange={setTerm} placeholder="Client, street, suburb or number" />

      {board.error ? (
        <div className={cardClass}>
          <ErrorState message={errorMessage(board.error)} onRetry={() => void board.refetch()} />
        </div>
      ) : board.isPending ? (
        <div className={cardClass}>
          <SkeletonRows rows={5} />
        </div>
      ) : days.size === 0 ? (
        <div className={cn(cardClass, 'py-4')}>
          <EmptyState
            title={term ? 'Nothing matches' : 'Nothing booked this week'}
            description={term ? 'Try a street, a suburb or the job number.' : undefined}
          />
        </div>
      ) : (
        [...days.values()].map((day) => (
          <Group
            key={day.date.toDateString()}
            label={relativeDay(day.date)}
            aside={`${day.visits.length} ${day.visits.length === 1 ? 'job' : 'jobs'}`}
          >
            {day.visits.map((visit) => {
              const lead = visit.assignments[0]?.user
              return (
                <Link key={visit.id} to={jobHref(visit.job.id)} className={rowClass}>
                  <div className="w-[70px] shrink-0 whitespace-nowrap">
                    <p className="text-[15px] font-semibold tabular-nums text-ink-900">
                      {formatTime(visit.startAt)}
                    </p>
                    <p className="text-xs tabular-nums text-ink-500">
                      {lengthOf(visit.startAt, visit.endAt)}
                    </p>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[16px] font-semibold text-ink-900">
                      {visit.job.client.name}
                    </p>
                    <p className="truncate text-[13px] text-ink-500">
                      {workAndSuburb(visit.job, visit.job.site?.address) ||
                        formatJobNumber(visit.job)}
                    </p>
                  </div>
                  {showEveryone ? (
                    lead ? (
                      <Avatar name={lead.name} colour={lead.colour} size="md" />
                    ) : (
                      <span className="text-xs font-semibold text-red-700">No tech</span>
                    )
                  ) : null}
                  <VisitBadge status={visit.status} />
                </Link>
              )
            })}
          </Group>
        ))
      )}

      {can('JOB_VIEW_ALL') ? (
        <Link
          to="/worklist"
          className="px-1 text-center text-[14px] font-medium text-brand-700"
        >
          Open the office worklist
        </Link>
      ) : null}

      <NewJobDialog open={newJobOpen} onClose={() => setNewJobOpen(false)} />
    </FieldScreen>
  )
}
